import { useTranslation } from "react-i18next";
import placeholder from "../assets/noimageplaceholder.svg";
import { ActiveEntity, ActiveEntityType, DynamicDataContext } from "../build/activeEntityType";
import { DynamicData, DynamicDataKey, hubDynamicDataKey, isKeyAllowedInContext } from "../build/dynamicData";

/** Dynamic data keys that are plain counts/quantities (e.g. stock, quantity), not monetary amounts in minor units. */
const PLAIN_NUMBER_KEYS: Set<DynamicDataKey> = new Set([
    `${ActiveEntityType.PRODUCT}.stock`,
    `${ActiveEntityType.PRODUCT}.quantity`,
    `${ActiveEntityType.CART}.numberOfProductsInCart`
]);
import { TimeFormat } from "../build/types";
import { useDynamicDataSection } from "../Contexts/DynamicDataTypeContext";
import { dynamicDataSpecialCases, formatTime, getValueFromObject } from "../utils/dynamicDataHelpers";

interface ProcessDynamicDataProps {
    dynamicDataString: DynamicDataKey;
    dataContext: DynamicDataContext;
    allData?: Record<string, any>;
    customEntity?: ActiveEntity;
    formatAmount?: (amount: number, decimalizable?: boolean) => string;
    formatCurrency?: (amount: number) => string;
    fallback?: string;
    fromBuilder?: boolean;
    t: (key: string) => string;
    hubDynamicData?: {
        buildSettings?: Record<string, string> | null;
        buildSettingsKey?: Record<string, string> | null;
    };
    timeFormat?: TimeFormat;
}

export const processDynamicData = ({
    formatAmount,
    formatCurrency,
    dynamicDataString,
    fallback,
    fromBuilder,
    t,
    dataContext,
    hubDynamicData,
    customEntity,
    allData,
    timeFormat
}: ProcessDynamicDataProps): { dynamicData: string | undefined } => {
    const formatA = formatAmount || ((amount: number) => amount);
    const formatC = formatCurrency || ((amount: number) => amount);
    let dynamicData: string | number | undefined | Date = undefined;

    const getFallback = (isAllowed?: boolean) => {
        const dataInfo = DynamicData[dynamicDataString];
        if (dataInfo?.dataType === "image") return fallback || placeholder;
        if (isAllowed === false) return "N/A";
        return fromBuilder ? t(`dynamicDataString.${dynamicDataString}`) : fallback || "\u200B  ";
    };

    const keysArray = dynamicDataString.split(".");
    const isHubDynamicData = keysArray[0] === hubDynamicDataKey;

    if (!isHubDynamicData && dataContext && !isKeyAllowedInContext(dynamicDataString, dataContext)) {
        return { dynamicData: getFallback(false) };
    }

    if (isHubDynamicData) {
        const dynamicData =
            hubDynamicData?.buildSettings?.[dynamicDataString] || hubDynamicData?.buildSettingsKey?.[dynamicDataString];
        return { dynamicData };
    }

    const isNotNested = dataContext === "general" || keysArray[0] === dataContext;
    const keyToAccess = isNotNested ? keysArray[0] : dataContext;
    const dataObj = customEntity || allData![keyToAccess];

    isNotNested && keysArray.shift();

    if (dynamicDataSpecialCases[dynamicDataString]) {
        const relevantObj = dataObj ? (isNotNested ? dataObj : dataObj[keysArray[0]]) : {};
        const specialCaseFunction = dynamicDataSpecialCases[dynamicDataString];
        dynamicData = specialCaseFunction(relevantObj, formatC);
    } else if (dataObj) {
        const valueFromObject = getValueFromObject(keysArray, dataObj);
        if (typeof valueFromObject !== "object" || valueFromObject instanceof Date) {
            dynamicData = valueFromObject;
        }
    }

    if (dynamicData === undefined || dynamicData === null || dynamicData === "" || Number.isNaN(dynamicData)) {
        dynamicData = getFallback();
    } else {
        const dataPrototype = DynamicData[dynamicDataString].dataType;
        if (dataPrototype === "currency") {
            dynamicData = formatC(dynamicData as number);
        } else if (dataPrototype === "number" && dynamicData !== "∞") {
            // Stock, quantity, and other counts are plain numbers — do not treat as minor units
            const num = dynamicData as number;
            dynamicData = PLAIN_NUMBER_KEYS.has(dynamicDataString) ? String(num) : formatA(num, false);
        } else if (dataPrototype === "time") {
            dynamicData = formatTime(dynamicData as Date, timeFormat || (dynamicDataString === "time" ? "HH:mm" : "dd/MM/yyyy"));
        }
    }

    return { dynamicData: dynamicData as string | undefined };
};

interface useDynamicDataProps {
    dynamicDataString?: DynamicDataKey;
    customEntity?: ActiveEntity;
    formatAmount?: (amount: number, decimalizable?: boolean) => string;
    formatCurrency?: (amount: number) => string;
    fromBuilder?: boolean;
    fallback?: string;
    timeFormat?: TimeFormat;
    hubDynamicData?: {
        buildSettings?: Record<string, string> | null;
        buildSettingsKey?: Record<string, string> | null;
    };
}

const useDynamicData = ({
    dynamicDataString,
    customEntity,
    formatAmount,
    formatCurrency,
    fromBuilder,
    timeFormat,
    fallback,
    hubDynamicData
}: useDynamicDataProps) => {
    const { t } = useTranslation();
    const { dynamicDataScope: dataContext, dynamicDataObject: allData } = useDynamicDataSection();

    if (!dynamicDataString) {
        return { dynamicData: undefined };
    }

    const { dynamicData } = processDynamicData({
        formatAmount,
        formatCurrency,
        dynamicDataString,
        fallback,
        fromBuilder,
        t,
        dataContext,
        hubDynamicData,
        customEntity,
        allData,
        timeFormat
    });

    return { dynamicData };
};

export default useDynamicData;
